import type { ParsedDocument } from "@magic-md/core";
import { Loader2, Square, Zap } from "lucide-react";
import { memo, type MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

function countExecutableUnits(parsedDocument: ParsedDocument | null): number {
    if (!parsedDocument) return 0;
    return (
        parsedDocument.definitions.size +
        (parsedDocument.forDefinitions?.size ?? 0) +
        (parsedDocument.contentDefinitions?.size ?? 0) +
        (parsedDocument.imports?.length ?? 0) +
        (parsedDocument.uses?.length ?? 0)
    );
}

function getFirstExecutableUnitId(parsedDocument: ParsedDocument | null): string | null {
    if (!parsedDocument) return null;
    return (
        parsedDocument.definitions.keys().next().value ??
        parsedDocument.forDefinitions?.keys().next().value ??
        parsedDocument.contentDefinitions?.keys().next().value ??
        parsedDocument.imports?.[0]?.namespace ??
        parsedDocument.uses?.[0]?.id ??
        null
    );
}

function getExecutableSummary(
    parsedDocument: ParsedDocument | null,
): { executableCount: number; fallbackRunAllUnitId: string | null } {
    return {
        executableCount: countExecutableUnits(parsedDocument),
        fallbackRunAllUnitId: getFirstExecutableUnitId(parsedDocument),
    };
}

function stopTabEvent(event: { stopPropagation: () => void }) {
    event.stopPropagation();
}

const MIN_OPTIMISTIC_RUN_FEEDBACK_MS = 450;
const RUN_ICON_SIZE = 12;
const START_ICON_SIZE = 11;
const STOP_ICON_SIZE = 10;

function findRunningUnitId(
    maps: Array<Iterable<[string, { status: string }]>>,
): string | null {
    for (const entries of maps) {
        for (const [unitId, entry] of entries) {
            if (entry.status === "running") return unitId;
        }
    }
    return null;
}

interface DocumentRunAllIdleButtonProps {
    tabId: string;
    executableCount: number;
    className?: string;
    visible: boolean;
    onRun: () => void;
}

const DocumentRunAllIdleButton = memo(function DocumentRunAllIdleButton({
    tabId,
    executableCount,
    className,
    visible,
    onRun,
}: DocumentRunAllIdleButtonProps) {
    const idleWidthClass = executableCount > 1 ? "w-7" : "w-5";
    const idleHoverWidthClass = executableCount > 1
        ? "group-hover:w-7 group-focus-within:w-7"
        : "group-hover:w-5 group-focus-within:w-5";
    const wrapperClassName = visible
        ? cn("ml-0.5 opacity-100 pointer-events-auto", idleWidthClass)
        : cn(
            "ml-0 w-0 opacity-0 pointer-events-none group-hover:ml-0.5 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:ml-0.5 group-focus-within:opacity-100 group-focus-within:pointer-events-auto",
            idleHoverWidthClass,
        );
    const buttonClassName = cn(
        "inline-flex h-5 w-full shrink-0 items-center justify-center gap-0.5 rounded px-1 text-[10px] font-medium leading-none whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
    );
    const commonEventHandlers = {
        onPointerDown: stopTabEvent,
        onMouseDown: stopTabEvent,
        onKeyDown: stopTabEvent,
    };

    return (
        <span
            className={cn(
                "inline-flex h-5 shrink-0 overflow-hidden transition-[width,margin,opacity] duration-150",
                wrapperClassName,
            )}
        >
            <button
                type="button"
                data-testid={`document-run-all-${tabId}`}
                {...commonEventHandlers}
                onClick={(event) => {
                    event.stopPropagation();
                    onRun();
                }}
                className={cn(
                    buttonClassName,
                    "theme-accent-icon-button",
                )}
                style={getThemeAccentStyle("success")}
                title={`Run All for this document (${executableCount} items)`}
                aria-label={`Run All for this document (${executableCount} items)`}
            >
                <Zap size={RUN_ICON_SIZE} className="shrink-0" strokeWidth={2.25} />
                {executableCount > 1 && (
                    <span className="tabular-nums opacity-70">{executableCount}</span>
                )}
            </button>
        </span>
    );
});

interface TrackedRunAllControlProps {
    tabId: string;
    className?: string;
    executableCount: number;
    fallbackRunAllUnitId: string | null;
    forceVisible: boolean;
    showStatusLabel: boolean;
    isBackgroundRunning: boolean;
    optimisticRunPending: boolean;
    onCancelBackgroundRunning?: () => void;
    onRun: () => void;
}

const TrackedRunAllControl = memo(function TrackedRunAllControl({
    tabId,
    className,
    executableCount,
    fallbackRunAllUnitId,
    forceVisible,
    showStatusLabel,
    isBackgroundRunning,
    optimisticRunPending,
    onCancelBackgroundRunning,
    onRun,
}: TrackedRunAllControlProps) {
    const runState = usePluginExecutionStore(
        useShallow((s) => {
            const sessionId = s.tabSessionMap.get(tabId);
            const isPending = s.runAllPendingTabIds.has(tabId);
            if (!sessionId) {
                return {
                    isRunning: isPending,
                    isRunAllRunning: isPending,
                    currentUnitId: null as string | null,
                    progress: null as { current: number; total: number; currentId: string | null } | null,
                    isStarting: isPending,
                    isActiveSession: false,
                };
            }
            if (s.sessionId === sessionId) {
                const runningUnitId = findRunningUnitId([
                    s.executions,
                    s.forExecutions,
                    s.contentExecutions,
                    s.importExecutions,
                    s.useExecutions,
                ]);
                const isRunAllRunning = isPending || s.isRunningAll;
                return {
                    isRunning: isRunAllRunning || !!runningUnitId,
                    isRunAllRunning,
                    currentUnitId: s.runAllProgress?.currentId ?? runningUnitId,
                    progress: s.runAllProgress,
                    isStarting: isPending && !s.isRunningAll,
                    isActiveSession: true,
                };
            }
            const snapshot = s.sessionExecutionCache.get(sessionId);
            const runningUnitId = snapshot
                ? findRunningUnitId([
                    snapshot.executions,
                    snapshot.forExecutions,
                    snapshot.contentExecutions,
                    snapshot.importExecutions,
                    snapshot.useExecutions,
                ])
                : null;
            const snapshotRunAllRunning = snapshot?.runAllState?.isRunning ?? false;
            const isRunAllRunning = isPending || snapshotRunAllRunning;
            return {
                isRunning: isRunAllRunning || !!runningUnitId,
                isRunAllRunning,
                currentUnitId: snapshot?.runAllState?.progress?.currentId ?? runningUnitId,
                progress: snapshot?.runAllState?.progress ?? null,
                isStarting: isPending && !snapshotRunAllRunning,
                isActiveSession: false,
            };
        }),
    );

    const isExecuting = runState.isRunning || isBackgroundRunning || optimisticRunPending;
    const isStarting =
        runState.isStarting ||
        (optimisticRunPending && !runState.isRunning && !isBackgroundRunning);
    const displayUnitId =
        runState.currentUnitId ??
        ((runState.isRunAllRunning || optimisticRunPending)
            ? fallbackRunAllUnitId
            : null);
    if (executableCount <= 0 && !isExecuting) return null;

    const visible = forceVisible || isExecuting;
    const progressLabel =
        runState.progress && runState.progress.total > 0
            ? `${runState.progress.current}/${runState.progress.total}`
            : "";
    const statusLabel = showStatusLabel
        ? [progressLabel, displayUnitId].filter(Boolean).join(" ")
        : "";
    const executingWidthClass = executableCount > 1 ? "w-7" : "w-5";
    const wrapperClassName = cn(
        "ml-0.5 opacity-100 pointer-events-auto",
        isExecuting ? (statusLabel ? "max-w-[160px]" : executingWidthClass) : "",
    );
    const buttonClassName = cn(
        "inline-flex h-5 shrink-0 items-center justify-center gap-0.5 rounded px-1 text-[10px] font-medium leading-none whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        statusLabel ? "w-5" : "w-full",
        className,
    );
    const commonEventHandlers = {
        onPointerDown: stopTabEvent,
        onMouseDown: stopTabEvent,
        onKeyDown: stopTabEvent,
    };

    if (!isExecuting) {
        return (
            <DocumentRunAllIdleButton
                tabId={tabId}
                executableCount={executableCount}
                className={className}
                visible={visible}
                onRun={onRun}
            />
        );
    }

    const title = isStarting
        ? "Starting this document"
        : displayUnitId
            ? `Running ${displayUnitId}${progressLabel ? ` (${progressLabel})` : ""}. Click to stop.`
            : "Stop running this document";
    const handleStatusButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (isStarting) return;
        const store = usePluginExecutionStore.getState();
        if (runState.isRunAllRunning || optimisticRunPending) {
            store.cancelRunAllForTab(tabId);
            return;
        }
        if (runState.currentUnitId && runState.isActiveSession) {
            store.cancelExecution(runState.currentUnitId);
            return;
        }
        onCancelBackgroundRunning?.();
    };

    return (
        <span
            className={cn(
                "inline-flex h-5 shrink-0 overflow-hidden transition-[width,margin,opacity] duration-150",
                wrapperClassName,
            )}
        >
            <button
                type="button"
                aria-disabled={isStarting ? "true" : undefined}
                data-testid={`document-run-all-${tabId}`}
                {...commonEventHandlers}
                onClick={handleStatusButtonClick}
                className={cn(
                    buttonClassName,
                    "theme-accent-icon-button",
                    isStarting && "cursor-default",
                )}
                style={getThemeAccentStyle(isStarting ? "warning" : "error")}
                title={title}
                aria-label={isStarting ? "Starting this document" : "Stop running this document"}
            >
                {isStarting ? (
                    <Loader2 size={START_ICON_SIZE} className="shrink-0 animate-spin" />
                ) : (
                    <Square size={STOP_ICON_SIZE} className="shrink-0 fill-current" />
                )}
            </button>
            {statusLabel && (
                <span className="flex h-5 min-w-0 items-center truncate text-[10px] leading-5 text-muted-foreground/80">
                    {statusLabel}
                </span>
            )}
        </span>
    );
});

export const DocumentRunAllControl = memo(function DocumentRunAllControl({
    tabId,
    className,
    forceVisible = false,
    showStatusLabel = false,
    isBackgroundRunning = false,
    onCancelBackgroundRunning,
}: {
    tabId: string | null | undefined;
    className?: string;
    forceVisible?: boolean;
    showStatusLabel?: boolean;
    isBackgroundRunning?: boolean;
    onCancelBackgroundRunning?: () => void;
}) {
    const mountedRef = useRef(true);
    const feedbackTimerRef = useRef<number | null>(null);
    const [optimisticRunPending, setOptimisticRunPending] = useState(false);
    const executableSummary = useEditorStore(
        useShallow((s) => {
            const parsedDocument =
                (tabId ? s.parsedDocumentMap.get(tabId) : null) ??
                (tabId === s.activeTabId ? s.parsedDocument : null);
            return {
                ...getExecutableSummary(parsedDocument),
                isActiveTab: tabId === s.activeTabId,
            };
        }),
    );

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (feedbackTimerRef.current) {
                window.clearTimeout(feedbackTimerRef.current);
                feedbackTimerRef.current = null;
            }
        };
    }, []);

    const { executableCount, fallbackRunAllUnitId, isActiveTab } = executableSummary;
    const startRunAll = useCallback(() => {
        if (!tabId) return;
        const startedAt = Date.now();
        setOptimisticRunPending(true);
        let runPromise: Promise<unknown>;
        try {
            runPromise = Promise.resolve(usePluginExecutionStore.getState().runAllForTab(tabId));
        } catch {
            runPromise = Promise.resolve();
        }
        void runPromise
            .finally(() => {
                const remaining = Math.max(
                    0,
                    MIN_OPTIMISTIC_RUN_FEEDBACK_MS - (Date.now() - startedAt),
                );
                if (feedbackTimerRef.current) {
                    window.clearTimeout(feedbackTimerRef.current);
                }
                feedbackTimerRef.current = window.setTimeout(() => {
                    feedbackTimerRef.current = null;
                    if (mountedRef.current) {
                        setOptimisticRunPending(false);
                    }
                }, remaining);
            })
            .catch(() => {});
    }, [tabId]);

    if (!tabId) return null;

    if (forceVisible || isActiveTab || isBackgroundRunning || optimisticRunPending) {
        return (
            <TrackedRunAllControl
                tabId={tabId}
                className={className}
                executableCount={executableCount}
                fallbackRunAllUnitId={fallbackRunAllUnitId}
                forceVisible={forceVisible}
                showStatusLabel={showStatusLabel}
                isBackgroundRunning={isBackgroundRunning}
                optimisticRunPending={optimisticRunPending}
                onCancelBackgroundRunning={onCancelBackgroundRunning}
                onRun={startRunAll}
            />
        );
    }

    if (executableCount <= 0) return null;

    return (
        <DocumentRunAllIdleButton
            tabId={tabId}
            executableCount={executableCount}
            className={className}
            visible={false}
            onRun={startRunAll}
        />
    );
});

export const DocumentRunAllStatusControl = memo(function DocumentRunAllStatusControl({
    tabId,
    className,
    isBackgroundRunning = false,
    onCancelBackgroundRunning,
}: {
    tabId: string | null | undefined;
    className?: string;
    isBackgroundRunning?: boolean;
    onCancelBackgroundRunning?: () => void;
}) {
    if (!tabId || !isBackgroundRunning) return null;

    return (
        <TrackedRunAllControl
            tabId={tabId}
            className={className}
            executableCount={0}
            fallbackRunAllUnitId={null}
            forceVisible
            showStatusLabel={false}
            isBackgroundRunning={isBackgroundRunning}
            optimisticRunPending={false}
            onCancelBackgroundRunning={onCancelBackgroundRunning}
            onRun={() => {}}
        />
    );
});
